"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { Tabs as TabsPrimitive } from "radix-ui";
import * as React from "react";
import { JSX, useContext } from "react";

import { cn } from "~/lib/utils";

export const tabsListVariants = cva(
  "bg-muted text-muted-foreground inline-flex w-fit items-center justify-center rounded-sm",
  {
    variants: {
      size: {
        default: "h-9 p-[3px]",
        sm: "h-8 p-[3px]",
        xs: "h-7 p-0.5",
        "2xs": "h-6 p-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

export const tabsTriggerVariants = cva(
  "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-sm border border-transparent font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      size: {
        default: "px-2 py-1 text-sm [&_svg:not([class*='size-'])]:size-4",
        sm: "px-2 py-0.5 text-sm [&_svg:not([class*='size-'])]:size-4",
        xs: "px-1.5 py-0.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        "2xs": "px-1.5 py-0 text-xs [&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

type TabsListSize = VariantProps<typeof tabsListVariants>["size"];

const TabsSizeContext = React.createContext<TabsListSize>("default");

export function Tabs<TTab extends string>(
  props: Omit<React.ComponentProps<typeof TabsPrimitive.Root>, "value" | "defaultValue" | "onValueChange"> & {
    value?: TTab;
    defaultValue?: TTab;
    onValueChange?: (value: TTab) => void;
  },
): JSX.Element;
export function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn("flex flex-col gap-2", className)} {...props} />;
}

export function TabsList({
  className,
  size,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
  return (
    <TabsSizeContext.Provider value={size}>
      <TabsPrimitive.List data-slot="tabs-list" className={cn(tabsListVariants({ size }), className)} {...props} />
    </TabsSizeContext.Provider>
  );
}

export function TabsTrigger({
  className,
  size,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & VariantProps<typeof tabsTriggerVariants>) {
  const contextSize = useContext(TabsSizeContext);
  const resolvedSize = size ?? contextSize;

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ size: resolvedSize }), className)}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn("flex-1 outline-none", className)} {...props} />;
}
